import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Check, Info, AlertCircle } from "lucide-react";

export const Section = ({ title, subtitle, children }) => (
  <div className="animate-fade-up">
    <h2 className="font-heading text-2xl sm:text-[28px] font-bold tracking-tight leading-tight text-foreground">{title}</h2>
    {subtitle && <p className="text-sm leading-relaxed text-muted-foreground mt-2.5 max-w-xl">{subtitle}</p>}
    <div className="mt-8">{children}</div>
  </div>
);

export const FieldError = ({ children, testid }) => (
  <p className="flex items-start gap-1.5 text-xs font-medium text-destructive mt-1.5" data-testid={testid}>
    <AlertCircle className="h-3.5 w-3.5 mt-px shrink-0" />{children}
  </p>
);

// One consistent field wrapper: bold label, optional tooltip, helper text, inline error.
export const Field = ({ label, hint, tooltip, error, required, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="text-sm font-semibold text-foreground flex items-center gap-1.5">
      {label}
      {required && <span className="text-destructive">*</span>}
      {tooltip && (
        <span title={tooltip} className="text-muted-foreground cursor-help" data-testid={testid ? `${testid}-tooltip` : undefined}>
          <Info className="h-3.5 w-3.5" />
        </span>
      )}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-muted-foreground leading-relaxed">{hint}</p>}
    {error && <FieldError testid={testid ? `${testid}-error` : undefined}>{error}</FieldError>}
  </div>
);

export const OptionCard = ({ active, onClick, icon: Icon, label, hint, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`group relative w-full text-start rounded-2xl border p-4 transition-all duration-200 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      active
        ? "border-[color:var(--tharwy-lime-700)] bg-[color:var(--tharwy-lime)]/10 shadow-premium"
        : "border-border bg-card hover:border-[color:var(--tharwy-lime-700)]/50 hover:shadow-premium"
    }`}>
    <div className="flex items-center gap-3.5">
      {Icon && (
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
          active ? "bg-[color:var(--tharwy-ink-950)] text-[color:var(--tharwy-lime)]" : "bg-secondary text-foreground group-hover:bg-accent"}`}>
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-sm leading-snug">{label}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{hint}</div>}
      </div>
      <span className={`ms-auto h-6 w-6 rounded-full flex items-center justify-center border transition-all shrink-0 ${
        active ? "bg-[color:var(--tharwy-ink-950)] border-[color:var(--tharwy-ink-950)] text-[color:var(--tharwy-lime)] scale-100" : "border-border text-transparent scale-90"}`}>
        <Check className="h-4 w-4" />
      </span>
    </div>
  </button>
);

export const Choice = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-full px-5 min-h-11 text-sm font-semibold border transition-colors ${
      active ? "bg-[color:var(--tharwy-ink-950)] text-white border-[color:var(--tharwy-ink-950)]"
             : "bg-card border-border hover:bg-accent"}`}>
    {children}
  </button>
);

export const SliderField = ({ label, value, display, min, max, step, onChange, testid, hint }) => (
  <div>
    <div className="flex items-center justify-between mb-3 gap-3">
      <span className="text-sm font-semibold">{label}</span>
      <span className="numeric text-sm font-bold rounded-full bg-[color:var(--tharwy-ink-950)] text-[color:var(--tharwy-lime)] px-3.5 py-1.5"
        data-testid={`${testid}-value`}>{display}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={testid} />
    <div className="flex justify-between text-[11px] text-muted-foreground mt-2">
      <span>${(min / 1000).toLocaleString()}k</span><span>${(max / 1000).toLocaleString()}k+</span>
    </div>
    {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
  </div>
);

export const TextField = ({ value, onChange, format, ...rest }) => (
  <Input value={value} onChange={(e) => onChange(format ? format(e.target.value) : e.target.value)}
    className="h-11 rounded-xl" {...rest} />
);

// ---- Auto-formatters: fewer typos, no silent rejections -------------------
export const formatPhone = (v) => {
  const digits = v.replace(/[^\d+]/g, "");
  const plus = digits.startsWith("+");
  const n = digits.replace(/\+/g, "");
  const groups = n.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  return (plus ? "+" : "") + groups;
};

export const formatDocNumber = (v) => v.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 20);
export const formatPostal = (v) => v.toUpperCase().replace(/[^A-Z0-9\s-]/g, "").slice(0, 12);
export const formatName = (v) => v.replace(/[^\p{L}\s'-]/gu, "").replace(/\s{2,}/g, " ");
